import React, { useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { api, formatApiError } from "@/lib/api";
import { ImageSquare, X } from "@phosphor-icons/react";
import { toast } from "sonner";

const MAX_LEN = 4000;
const SHOT_MAX_SIDE = 1600;

// Shrink a picked image to a JPEG data URL so uploads stay small.
function shrink(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const k = Math.min(1, SHOT_MAX_SIDE / Math.max(img.width, img.height));
      const c = document.createElement("canvas");
      c.width = Math.round(img.width * k);
      c.height = Math.round(img.height * k);
      const ctx = c.getContext("2d");
      ctx.fillStyle = "#fff";
      ctx.fillRect(0, 0, c.width, c.height);
      ctx.drawImage(img, 0, 0, c.width, c.height);
      URL.revokeObjectURL(url);
      resolve(c.toDataURL("image/jpeg", 0.82));
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("bad image"));
    };
    img.src = url;
  });
}

// What the player is using, so a bug report says where it happened.
function deviceInfo() {
  const cap = typeof window !== "undefined" ? window.Capacitor : null;
  let platform = "web";
  try {
    if (cap?.isNativePlatform?.()) platform = `${cap.getPlatform()} app`;
  } catch { /* ignore */ }
  return {
    platform,
    user_agent: navigator.userAgent,
    screen: `${window.screen?.width}x${window.screen?.height}`,
    window: `${window.innerWidth}x${window.innerHeight}`,
    pixel_ratio: window.devicePixelRatio || 1,
    language: navigator.language,
    theme: document.documentElement.classList.contains("dark") ? "dark" : "light",
  };
}

export default function SendFeedback({ open, onOpenChange }) {
  const location = useLocation();
  const [message, setMessage] = useState("");
  const [shot, setShot] = useState("");
  const [sending, setSending] = useState(false);
  const fileRef = useRef(null);

  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try {
      setShot(await shrink(file));
    } catch {
      toast.error("Couldn't read that picture");
    }
  };

  const send = async () => {
    if (!message.trim()) {
      toast.error("Please write something first");
      return;
    }
    setSending(true);
    try {
      await api.post("/feedback", {
        message: message.trim(),
        page: location.pathname + location.search,
        device: deviceInfo(),
        screenshot: shot,
      });
      toast.success("Thanks! Your feedback was sent.");
      setMessage("");
      setShot("");
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Couldn't send, please try again");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg bg-surface border-border" data-testid="send-feedback-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-tight">Send feedback</DialogTitle>
          <DialogDescription className="text-muted-foreground">
            Found a bug or have an idea? Tell us and we'll read every message.
          </DialogDescription>
        </DialogHeader>

        <Textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="What happened, or what would you like to see?"
          maxLength={MAX_LEN}
          autoFocus
          className="bg-background min-h-[140px] resize-none"
          data-testid="send-feedback-message"
        />

        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={pick} />
        {shot ? (
          <div className="relative w-fit">
            <img src={shot} alt="Screenshot" className="max-h-40 rounded-lg border border-border" />
            <button
              type="button"
              onClick={() => setShot("")}
              className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-foreground text-background flex items-center justify-center"
              aria-label="Remove screenshot"
              data-testid="send-feedback-remove-shot"
            >
              <X size={12} weight="bold" />
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            className="w-fit flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
            data-testid="send-feedback-add-shot"
          >
            <ImageSquare size={18} /> Add a screenshot (optional)
          </button>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={sending}>
            Cancel
          </Button>
          <Button
            onClick={send}
            disabled={sending}
            className="bg-brand hover:bg-brand-hover text-brand-foreground rounded-full px-6"
            data-testid="send-feedback-submit"
          >
            {sending ? "Sending…" : "Send"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
